// @flow
import * as React from 'react';
import styled from 'styled-components';
import { trackClicked } from 'utils/analytics';
import { RouterLink } from 'components/Link';
import Text from 'components/Text';
import Divider from 'components/Divider';
import Flex from 'components/Flex';
import Box from 'components/Box';
import Hide from 'components/Hide';
import ListIcon from 'list.svg';
import { ChevronLeftIcon, ChevronRightIcon } from 'components/ChevronIcon';
import { colors } from '../theme';

const getUrl = result =>
  result
    ? result.type === 'IMPORTED'
      ? `/${USER_LOCALE}/imported-action-opps/${result.source
          .replace(/_/g, '-')
          .toLowerCase()}/${result.objectID.replace(`${result.source}_`, '')}`
      : result.url[USER_LOCALE]
    : '';

const Link = styled(RouterLink)`
  letter-spacing: 0.44px;
`;

const BackLink = styled(Link)`
  display: flex;
  align-items: center;
  svg {
    width: 17px;
    height: 14.45px;
    vertical-align: middle;
    margin-right: 8px;
    * {
      fill: ${colors.battleshipGrey};
    }
  }
`;

const ResultsCount = styled.div`
  padding: 0 10px;
  margin: 0 10px;
  border: 1px solid ${colors.silver};
  border-width: 0 1px;
  color: ${colors.battleshipGrey};
`;

const LinkText = styled(
  ({ text = 'previous', className }: { text: string, className: string }) => (
    <Flex alignItems="center" className={className}>
      {(() => {
        switch (text) {
          case 'next':
            return (
              <>
                <span>{getText('Next')}</span>
                <ChevronRightIcon />
              </>
            );
          case 'previous':
          default:
            return (
              <>
                <ChevronLeftIcon />
                <span>{getText('Previous')}</span>
              </>
            );
        }
      })()}
    </Flex>
  )
)`
  > :not(:first-child) {
    margin-left: 5px;
  }
  svg {
    path,
    polyline {
      fill: currentColor;
    }
  }
`;

const SearchContextHeader = ({
  searchResultsPath,
  previousResult,
  previousResultPageIndex,
  nextResult,
  nextResultPageIndex,
  resultIndex,
  totalResults,
  setPageIndex,
  divider = true,
  pathToResults,
  originatingPathname,
  ...props
}: {
  searchResultsPath: Object,
  previousResult: ?Object,
  previousResultPageIndex: ?number,
  nextResult: ?Object,
  nextResultPageIndex: ?number,
  resultIndex: number,
  totalResults: number,
  setPageIndex: Function,
  divider: boolean,
  pathToResults?: string,
  originatingPathname?: string
}) => {
  const nextUrl = getUrl(nextResult);
  const previousUrl = getUrl(previousResult);
  return (
    <Text
      fontSize="14px"
      color="battleshipGrey"
      letterSpacing=".26px"
      {...props}
    >
      <Flex justifyContent="space-between">
        <BackLink
          to={searchResultsPath}
          onClick={() =>
            trackClicked('Back To Results', { header_position: 'top' })
          }
          data-qa-id="search-context-back-to-search"
        >
          <ListIcon />
          {getText('Back to results')}
        </BackLink>
        <Flex alignItems="center">
          {previousResult ? (
            <Link
              data-qa-id="search-context-previous-result"
              to={{
                pathname: previousUrl,
                state: {
                  showSearchContext: true,
                  pathToResults,
                  originatingPathname
                }
              }}
              onClick={() => {
                trackClicked('Previous Listing', {
                  org_id: previousResult.orgID,
                  org_type: previousResult.orgType,
                  result_id: previousResult.objectID,
                  header_position: 'top'
                });
                if (previousResultPageIndex !== null) {
                  setPageIndex(previousResultPageIndex, false);
                }
              }}
            >
              <LinkText text="previous" />
            </Link>
          ) : (
            <LinkText text="previous" />
          )}
          <Hide xs sm>
            <ResultsCount>
              {resultIndex + 1} of {totalResults}
            </ResultsCount>
          </Hide>
          <Hide md lg xl>
            <Box mx="8px">|</Box>
          </Hide>
          {nextResult ? (
            <Link
              data-qa-id="search-context-next-result"
              to={{
                pathname: nextUrl,
                state: {
                  showSearchContext: true,
                  pathToResults,
                  originatingPathname
                }
              }}
              onClick={() => {
                trackClicked('Next Listing', {
                  org_id: nextResult.orgID,
                  org_type: nextResult.orgType,
                  result_id: nextResult.objectID,
                  header_position: 'top'
                });
                if (nextResultPageIndex !== null) {
                  setPageIndex(nextResultPageIndex, false);
                }
              }}
            >
              <LinkText text="next" />
            </Link>
          ) : (
            <LinkText text="next" />
          )}
        </Flex>
      </Flex>
      {divider && <Divider />}
    </Text>
  );
};

export default SearchContextHeader;
